<template>
  <div class="multi-lang-input">
    <div class="input-header" v-if="label">
      <span class="label">{{ label }}</span>
    </div>

    <!-- 语言选择器和输入框 -->
    <div class="language-input-group">
      <div class="lang-selector" v-if="showSelect">
        <el-select
          v-model="currentLanguage"
          :placeholder="placeholder || $t('选择语言')"
          @change="handleLanguageSwitch"
          :style="{ width: selectorWidth }">
          <el-option
            v-for="lang in languageList"
            :key="lang.code"
            :label="getLanguageOptionLabel(lang)"
            :value="lang.code">
            <span class="lang-option">
              <span class="lang-name">{{ lang.name }}</span>
              <span v-if="isLanguageFilled(lang.code)" class="filled-indicator">✓</span>
            </span>
          </el-option>
        </el-select>
      </div>
      <div class="lang-input">
        <el-input
          v-model="currentLanguageName"
          :placeholder="inputPlaceholder || $t('请输入内容')"
          @input="updateCurrentLanguageName"
          clearable>
        </el-input>
      </div>
    </div>

    <!-- 填写状态提示 -->
    <div class="fill-status" v-if="showStatus && getFilledLanguagesCount() > 0">
      <span class="status-text">
        {{ $t('已填写') }}: {{ getFilledLanguagesCount() }}/{{ languageList.length }} {{ $t('种语言') }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiLangInput',
  props: {
    // 双向绑定的数据，格式: {zh: '', en: ''}
    value: {
      type: Object,
      default: () => ({})
    },
    // 语言列表配置
    languageList: {
      type: Array,
      default: () => [
        { code: 'zh', name: '中文' },
        { code: 'en', name: 'English' }
      ]
    },
    // 标签文本
    label: {
      type: String,
      default: ''
    },
    // 语言选择器占位符
    placeholder: {
      type: String,
      default: ''
    },
    // 输入框占位符
    inputPlaceholder: {
      type: String,
      default: ''
    },
    // 选择器宽度
    selectorWidth: {
      type: String,
      default: '120px'
    },
    // 是否显示填写状态
    showStatus: {
      type: Boolean,
      default: true
    },
    // 默认语言
    defaultLanguage: {
      type: String,
      default: ''
    },
    showSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 当前选中的语言
      currentLanguage: '',
      // 当前语言的内容
      currentLanguageName: ''
    }
  },
  watch: {
    value: {
      handler(newVal) {
        // 只在当前语言没有设置时才初始化
        if (!this.currentLanguage) {
          this.initializeCurrentLanguage()
        } else {
          // 如果当前语言已设置，只更新输入框内容
          this.handleLanguageSwitch()
        }
      },
      immediate: true,
      deep: true
    },
    // 监听 defaultLanguage 变化
    defaultLanguage: {
      handler(newLang) {
        if (newLang && this.languageList.some(lang => lang.code === newLang)) {
          this.currentLanguage = newLang
          this.handleLanguageSwitch()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.initializeCurrentLanguage()
  },
  methods: {
    // 初始化当前语言
    initializeCurrentLanguage() {
      if (this.defaultLanguage && this.languageList.some(lang => lang.code === this.defaultLanguage)) {
        this.currentLanguage = this.defaultLanguage
      } else if (this.languageList.length > 0) {
        this.currentLanguage = this.languageList[0].code
      }
      this.handleLanguageSwitch()
    },

    // 处理语言切换
    handleLanguageSwitch() {
      this.currentLanguageName = this.value[this.currentLanguage] || ''
      // 触发事件通知父组件语言已切换
      this.$emit('language-change', this.currentLanguage)
    },

    // 更新当前语言的内容
    updateCurrentLanguageName(value) {
      const newValue = { ...this.value }
      newValue[this.currentLanguage] = value
      this.$emit('input', newValue)
    },

    // 获取语言选项标签
    getLanguageOptionLabel(lang) {
      const isFilled = this.isLanguageFilled(lang.code)
      return isFilled ? `${lang.name} ✓` : lang.name
    },

    // 检查语言是否已填写
    isLanguageFilled(code) {
      return this.value[code] && this.value[code].trim() !== ''
    },

    // 获取已填写的语言数量
    getFilledLanguagesCount() {
      return Object.values(this.value).filter(name => name && name.trim() !== '').length
    },

    // 获取语言名称
    getLanguageName(code) {
      const lang = this.languageList.find(l => l.code === code)
      return lang ? lang.name : code
    }
  }
}
</script>

<style scoped>
.multi-lang-input {
  width: 100%;
}

.input-header {
  margin-bottom: 12px;
}

.input-header .label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.language-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.language-input-group .lang-selector {
  flex-shrink: 0;
}

.language-input-group .lang-input {
  flex: 1;
}

.fill-status {
  margin-top: 8px;
  text-align: right;
}

.fill-status .status-text {
  font-size: 12px;
  color: #666;
  background: #f0f9ff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e1f5fe;
}

/* 语言选项样式 */
.lang-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.lang-option .lang-name {
  flex: 1;
}

.lang-option .filled-indicator {
  color: #52c41a;
  font-weight: bold;
  margin-left: 8px;
}
</style>
